import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/i18n/LanguageContext";

const BlogPost = () => {
    const { slug } = useParams();
    const { t } = useLanguage();
    const B = t.blog;
    const posts = B.posts || [];
    const post = posts.find((p) => p.slug === slug);

    useEffect(() => {
        window.scrollTo({ top: 0 });
        if (post?.title) document.title = `${post.title} · ELLA Journal`;
    }, [slug, post]);

    if (!post) {
        return <Navigate to="/journal" replace />;
    }

    const others = posts.filter((p) => p.slug !== slug).slice(0, 2);

    return (
        <div data-testid="blog-post-page" className="bg-ella-cream min-h-screen">
            <Header />
            <main className="pt-32 md:pt-40 pb-24">
                <article>
                    <div className="ella-container max-w-3xl">
                        <Link
                            to="/journal"
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-ella-muted hover:text-ella-ink transition-colors"
                            data-testid="blog-post-back"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                            {B.back_to_blog}
                        </Link>

                        <div className="mt-10">
                            <span className="ella-overline" data-testid="blog-post-category">
                                {post.category}
                            </span>
                            <h1
                                className="ella-headline text-4xl md:text-5xl lg:text-[58px] mt-4 text-ella-ink leading-[1.05]"
                                data-testid="blog-post-title"
                            >
                                {post.title}
                            </h1>
                            <div className="mt-6 flex items-center gap-3 text-[11px] text-ella-muted uppercase tracking-[0.2em]">
                                <span>
                                    {B.published_on} {post.date}
                                </span>
                                <span className="text-ella-gold/60">·</span>
                                <span className="inline-flex items-center gap-1">
                                    <Clock className="w-3 h-3" strokeWidth={1.5} />
                                    {post.read_minutes} {B.min_read}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="ella-container max-w-5xl mt-12">
                        <div className="aspect-[16/9] rounded-3xl overflow-hidden border border-ella-line shadow-[0_24px_60px_rgba(31,29,28,0.12)]">
                            <img
                                src={post.image}
                                alt={post.title}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>

                    <div className="ella-container max-w-3xl mt-14 prose-ella" data-testid="blog-post-body">
                        {post.body.map((para, i) => (
                            <p
                                key={i}
                                className={`text-ella-ink/85 font-light leading-[1.85] text-[18px] ${
                                    i === 0 ? "first-letter:font-display first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.9] first-letter:text-ella-gold-dark" : "mt-6"
                                }`}
                            >
                                {para}
                            </p>
                        ))}
                    </div>

                    {/* Footer signature */}
                    <div className="ella-container max-w-3xl mt-16 pt-10 border-t border-ella-line">
                        <p className="font-display italic text-xl text-ella-ink/80">
                            — Mercedes Köstel
                        </p>
                        <p className="ella-overline mt-2">
                            {t.about.sign_role}
                        </p>
                    </div>
                </article>

                {/* Related posts */}
                {others.length > 0 && (
                    <section className="mt-24 pt-20 border-t border-ella-line">
                        <div className="ella-container">
                            <p className="ella-overline">{B.overline}</p>
                            <h2 className="font-display text-3xl md:text-4xl mt-3 text-ella-ink">
                                {B.cta_all}
                            </h2>

                            <div className="mt-10 grid md:grid-cols-2 gap-8" data-testid="blog-post-related">
                                {others.map((post) => (
                                    <Link
                                        key={post.slug}
                                        to={`/journal/${post.slug}`}
                                        data-testid={`blog-related-${post.slug}`}
                                        className="group flex flex-col bg-white border border-ella-line rounded-3xl overflow-hidden hover:shadow-[0_24px_60px_rgba(31,29,28,0.12)] hover:border-ella-gold/40 transition-all duration-500"
                                    >
                                        <div className="relative aspect-[16/10] overflow-hidden">
                                            <img
                                                src={post.image}
                                                alt={post.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                            />
                                        </div>
                                        <div className="p-7">
                                            <p className="ella-overline">{post.category}</p>
                                            <h3 className="font-display text-2xl mt-3 text-ella-ink group-hover:text-ella-gold-dark transition-colors">
                                                {post.title}
                                            </h3>
                                            <div className="mt-4 inline-flex items-center gap-2 text-ella-gold-dark text-xs uppercase tracking-[0.24em]">
                                                {B.cta_read}
                                                <ArrowRight
                                                    className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform"
                                                    strokeWidth={1.5}
                                                />
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </section>
                )}
            </main>
            <Footer />
        </div>
    );
};

export default BlogPost;
